import { ICalculateOrderResponseEntity } from '../../model/api/price-calculator/calculate-order-response.entity.interface';
import { IInitializeOrderCalculatorResponseEntity } from '../../model/api/price-calculator/initialize-order-calculator-response.entity.interface';
import { IPriceCalculator } from '../../model/models/price-calculator/price-calculator.interface';
import { IInitializePriceCalculator } from '../../model/models/price-calculator/initialize-price-calculator.interface';
import { ICalculateOrderRequest } from '../../model/api/price-calculator/calculate-order-request.entity.interface';
import { IOrder } from '../../model/models/order/order-interface';
import { IDictionary } from '../../model/models/dictionary.interface';
import { ICurrency } from '../../model/models/currency/currency.interface';
import { FormControlMapper } from '../formbuilder/formcontrol-mapper';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { OrderMapper } from '../order/order.mapper';
import { LoggerProvider } from '../logger/logger';
import { APP_CONFIG, Region } from '../../environment';
import { FeatureFlagTypes } from '../../model/types/feature-flag-types';
import { UtilsProvider } from '../myria/utils-provider';
import { isCurrencyAllowedForCustomer, isStablecoin, STABLECOIN_ALLOWED_DELIVERY_METHODS } from '../../utils/stablecoin-mvp';

export class PriceCalculatorMapper {
  protected static loggedDebitCardPayout: boolean = false;
  protected static loggedDebitCardPayoutCountry = '';

  public static fromOrderToCalculateRequest(model: IOrder): ICalculateOrderRequest {
    const entity: ICalculateOrderRequest = {
      selections: {
        countryTo: model.countryTo.toUpperCase(),
        currencyTo: model.currencyTo,
        currencyFrom: model.currencyFrom,
        paymentMethod: model.paymentMethod,
        deliveryMethod: OrderMapper.mapApiDeliveryMethod(OrderMapper.effectiveDeliveryMethod(model)),
        amountFrom: (model.amountFrom) ? String(model.amountFrom) : null,
        amountTo: (model.amountTo && model.shouldCalcAmountFrom) ? String(model.amountTo) : null,
        shouldCalcAmountFrom: (model.amountTo) ? model.shouldCalcAmountFrom : false,
        shouldCalcVariableRates: model.shouldCalcVariableRates ? model.shouldCalcVariableRates : true,
        agentToId: (model.agentTo && model.agentTo.id) ? model.agentTo.id : (model.agentToLocation ? model.agentToLocation.agentId : null),
        agentToLocationId: model.agentToLocation ? model.agentToLocation.locationId : null,
        promoCode: (model.promoCode) ? model.promoCode : model.promoEngineCode,
        promoId: model.promoId,
        IsReferralPromo: model.IsReferralPromo,
        PromoTransIdLocked: model.PromoTransIdLocked,
        shouldCalcFeesForPaymentMethods: (model.paymentMethods) ? model.paymentMethods.map(method => method.value) : null,
      }
    };
    return entity;
  }

  public static fromInitializePriceCalculatorResponseEntity(entity: IInitializeOrderCalculatorResponseEntity, countriesWithCurrencies): IInitializePriceCalculator {
    const countries: IDictionary[] = [];

    const filterCurrenciesForStablecoinsMvp = (list: string[]): string[] =>
      (list || []).filter((code) => isCurrencyAllowedForCustomer(code));

    // This isn't ideal, unsure if we should make some constants for this or really if this will change or be re-used.
    const formFieldCountries = entity.formData['CalculatorForm'].formFields.filter(f => f.id === 'SendTo')[0];
    formFieldCountries.options.forEach(country => {
      if (country.value !== '') {
        const abbrev = country.value.toUpperCase();
        const match = countriesWithCurrencies.find(c => c.NameAbbrev === abbrev);

        // To ensure we are not rendering sanctioned countries for MY & SG
        if (APP_CONFIG.region.region === Region.MY || APP_CONFIG.region.region === Region.SG) {
          if (match) {
            countries.push({
              text: country.text,
              value: country.value.toUpperCase(),
              availableCurrencies: filterCurrenciesForStablecoinsMvp(match.AvailableCurrencies),
            });
          }
        } else {
            // We are compiling a list to search through for autocomplete, don't include default empty value.
            countries.push({
              text: country.text,
              value: country.value.toUpperCase(),
              availableCurrencies: match ? filterCurrenciesForStablecoinsMvp(match.AvailableCurrencies) : [],
            });

        }
      }
    });

    // Mash together multiple form responses.
    const fields = FormControlMapper.fromFormResponse(entity);
    
    return {
      availableSendToCountries: countries,
      fields: fields,
    }
  };

  public static fromPriceCalculatorResponseEntity(translate, entity: ICalculateOrderResponseEntity, extractedPaymentMethods = null): IPriceCalculator {
    const model: IPriceCalculator = {
      amountTo: entity.model.transferDetails.calculations.amountTo,
      amountFrom: entity.model.transferDetails.calculations.amountFrom,
      exchangeRate: entity.model.transferDetails.calculations.exchangeRate,
      totalAmount: entity.model.transferDetails.calculations.totalAmount,
      totalAmountUSD: entity.model.transferDetails.calculations.totalAmountUSD,
      taxAmount: entity.model.transferDetails.calculations.taxAmount,
      transferFee: entity.model.transferDetails.calculations.transferFee,
      originalCustomerFee: entity.model.transferDetails.calculations.originalCustomerFee,
      isAmountFromValid: entity.model.transferDetails.calculations.isAmountFromValid,
      minimumAmountFrom: entity.model.transferDetails.calculations.sendAmountLimit.minimumSendFromAmount,
      maximumAmountFrom: entity.model.transferDetails.calculations.sendAmountLimit.maximumSendFromAmount,
      showInlineError: entity.model.transferDetails.calculations.sendAmountLimit.showInlineError,
      decimalPrecision: entity.model.transferDetails.calculations.decimalPrecision,
      isPromoValid: entity.model.transferDetails.calculations.isPromoValid,
      promoAmount: entity.model.transferDetails.calculations.promoAmount,
      sendAmountUSD: entity.model.transferDetails.calculations.sendAmountUSD,
      variableRates: [],
      currencies: [],
      paymentMethods: [],
      deliveryMethods: [],
      primaryCurrency:  entity.model.transferDetails.calculations.splitCurrency1,
      secondaryCurrency: entity.model.transferDetails.calculations.splitCurrency2,
      primaryExchangeRate: entity.model.transferDetails.calculations.splitExchangeRate1,
      secondaryExchangeRate: entity.model.transferDetails.calculations.splitExchangeRate2,
      primaryReceiveAmount: entity.model.transferDetails.calculations.splitReceiveAmount1,
      secondaryReceiveAmount: entity.model.transferDetails.calculations.splitReceiveAmount2,
      payAgentId: entity.model.transferDetails.calculations.payAgentId,
      payAgentName: entity.model.transferDetails.calculations.payAgentName,
      correspondentPayoutFeeAmount:entity.model.transferDetails.calculations.correspondentPayoutFeeAmount,
      correspondentPayoutFeeCurrency:entity.model.transferDetails.calculations.correspondentPayoutFeeCurrency,
      estimatedAmountToAfterCorrespondentFees:entity.model.transferDetails.calculations.estimatedAmountToAfterCorrespondentFees,
      totalFeesAndTaxes:entity.model.transferDetails.calculations.totalFeesAndTaxes,
      transferFeeUSD: entity.model.transferDetails.calculations.transferFeeUSD,
      exchangeRatePromo: entity.model.transferDetails.calculations.exchangeRatePromo,
      calculatedRatePromo: entity.model.transferDetails.calculations.calculatedRatePromo,
      variableFees: [],
      isEarlyReleaseLikely: entity.model.transferDetails.calculations.isEarlyReleaseLikely === true,
    };

    if (entity.model.transferDetails.transferOptions.currencies) {
      const currencies = [];
      entity.model.transferDetails.transferOptions.currencies.forEach((currency) => {
        // Bad data check.
        if (currency.currencyCode === '') return;
        if (!isCurrencyAllowedForCustomer(currency.currencyCode)) return;
        currencies.push({
          isDefaultCurrency: currency.isDefaultCurrency,
          currencyCode: currency.currencyCode,
        });
      });

      // Override default currency for Haiti (HT) to USD instead of HTG
      // Only applies for BankDeposit delivery method
      // TODO: Remove this override when isDefaultCurrency is changed in the database
      const countryTo = entity.model.transferDetails.selections.countryTo;
      const deliveryMethod = entity.model.transferDetails.selections.deliveryMethod;
      if (countryTo === 'HT' && deliveryMethod === DeliveryMethodType.BankDeposit) {
        currencies.forEach((currency) => {
          if (currency.currencyCode === 'USD') {
            currency.isDefaultCurrency = true;
          }
          if (currency.currencyCode === 'HTG') {
            currency.isDefaultCurrency = false;
          }
        });
      }

      // sort currencies by default, descending
      currencies.sort((a: ICurrency, b: ICurrency) => {
        if (a.isDefaultCurrency) {
          return -1;
        } else if (b.isDefaultCurrency) {
          return 1;
        }
        return 0;
      })
      model.currencies = currencies;
    }

    if (entity.model.transferDetails.calculations.variableFees) {
    // check with Martin
      entity.model.transferDetails.calculations.variableFees.forEach((item) => {
        model.variableFees.push({
          originalCustomerFee: item.originalCustomerFee,
          customerFee: item.customerFee,
          paymentMethod: item.paymentMethod,
          paymentMethodID: item.paymentMethodID,
          isLowestFee:item.isLowestFee,
          isHidden: UtilsProvider.iCreditFeesCheck(item) ? true: false
        });
      });
    }

    if (extractedPaymentMethods && extractedPaymentMethods.length > 0) {
      // Use extractedPaymentMethods returned by Agent/paymentMethods to get the missing properties 
      if (model.variableFees && model.variableFees.length > 0) {
        extractedPaymentMethods.forEach((item) => {
          model.variableFees.forEach((variable) => {
            if (variable.paymentMethod === item.value) {
              model.paymentMethods.push({
                text: item.text,
                value: item.value,
                isFastest: item.isFastest,
                subTitle: item.subTitle,
                isHidden: item.isHidden,
                pspPaymentMethod: item.pspPaymentMethod,
                originalCustomerFee: variable.originalCustomerFee,
                customerFee: variable.customerFee,
                paymentMethod: variable.paymentMethod,
                paymentMethodID: variable.paymentMethodID,
                isLowestFee:variable.isLowestFee
              });
            }
          });
        });
      } else {
        // Handle the case where variableFees is null or empty
        extractedPaymentMethods.forEach((item) => {
          entity.model.transferDetails.transferOptions.paymentMethods.forEach((element) => {
            if (element.value === item.value) {
              model.paymentMethods.push({
                text: item.text,
                value: item.value,
                isFastest: item.isFastest,
                subTitle: item.subTitle,
                isHidden: item.isHidden,
                pspPaymentMethod: item.pspPaymentMethod,
                originalCustomerFee: item.originalCustomerFee,
                customerFee: item.customerFee,
                paymentMethod: item.paymentMethod,
                paymentMethodID: item.paymentMethodID,
              });
            }
          });
        });
      }
    }
    else {
      if (entity.model.transferDetails.transferOptions.paymentMethods) {
        // In case extractedPaymentMethods is null or empty use paymentMethods form calculate response 
        if (model.variableFees && model.variableFees.length > 0) {
          entity.model.transferDetails.transferOptions.paymentMethods.forEach((item) => {
            model.variableFees.forEach((variable) => {
              if (variable.paymentMethod === item.value) {
                model.paymentMethods.push({
                  text: item.text,
                  value: item.value,
                  originalCustomerFee: variable.originalCustomerFee,
                  customerFee: variable.customerFee,
                  paymentMethod: variable.paymentMethod,
                  paymentMethodID: variable.paymentMethodID,
                  isHidden: variable.isHidden
                });
              }
            });
          });
        } else {
         // Fall back to previous implementation
          entity.model.transferDetails.transferOptions.paymentMethods.forEach((item) => {
            model.paymentMethods.push({
              text: item.text,
              value: item.value,
            });
          });
        }
      }
    }

    if (entity.model.transferDetails.calculations.variableRates) {
      entity.model.transferDetails.calculations.variableRates.forEach((item) => {
        model.variableRates.push({
          value: item.value,
          exchangeRate: item.exchangeRate,
          payAgentId: item.payAgentId,
          payAgentName: item.payAgentName,
          isBestRate: item.isBestRate,
        });
      });
    }

    if (entity.model.transferDetails.transferOptions.deliveryMethods) {
      entity.model.transferDetails.transferOptions.deliveryMethods.forEach((item) => {
        model.variableRates.forEach((variable) => {
          if (variable.value === item.value) 
            model.deliveryMethods.push({
              text: item.text,
              value: item.value,
              exchangeRate: variable.exchangeRate,
              payAgentId: variable.payAgentId,
              payAgentName: variable.payAgentName,
              isBestRate: variable.isBestRate
          })      
        });
      })
    }

    const currencyToForStablecoinGate = entity.model.transferDetails.selections.currencyTo;
    const debitCardPayoutEnabled = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.DeliveryMethodDebitCardPayoutEnabled)
      && !isStablecoin(currencyToForStablecoinGate);
    if (debitCardPayoutEnabled && model.deliveryMethods) {
      if (!PriceCalculatorMapper.loggedDebitCardPayout)
        LoggerProvider.info(this, 'Debit Card Payout Delivery Method is enabled');
      PriceCalculatorMapper.loggedDebitCardPayout = true;

      const country = entity.model.transferDetails.selections.countryTo;
      const destinationCountries = ',' + UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.DeliveryMethodDebitCardPayoutDestinationCountries) + ',';
      if (destinationCountries.indexOf(',' + country + ',') >= 0) {
        if (country !== PriceCalculatorMapper.loggedDebitCardPayoutCountry)
          LoggerProvider.info(this, `Add debit card payout to delivery methods for country ${country} (allowed countries: ${destinationCountries})`);
        PriceCalculatorMapper.loggedDebitCardPayoutCountry = country;

        const bankDeposit = model.deliveryMethods.find((item) => item.value === DeliveryMethodType.BankDeposit.toString());
        if (bankDeposit) {
          model.deliveryMethods.push({
            ...bankDeposit,
            ...OrderMapper.getDeliveryMethodDebitCardDeposit(translate)
          });
        }
      }
    }

    if (isStablecoin(currencyToForStablecoinGate) && model.deliveryMethods) {
      model.deliveryMethods = model.deliveryMethods.filter(
        (m) => STABLECOIN_ALLOWED_DELIVERY_METHODS.indexOf(m.value) >= 0
      );
    }

    return model;
  }
}



// WEBPACK FOOTER //
// ./src/providers/price-calculator/price-calculator.mapper.ts